/**
 * `/collect/:token` — the public form. No account, no ring, nothing to learn:
 * a name, a date, optionally a photo. The seal beside the fields shows where
 * on the year the date lands.
 */
import { MONTH_NAMES, collectSubmitSchema, daysInMonth } from '@circle/shared';
import { ApiError, api, type CollectInfo } from '../api';
import { validate } from '../validate';
import type { PageContext, Teardown } from '../router';
import { RingSeal } from '../../ui/ring-seal';
import { PhotoPicker } from '../../ui/photo-picker';
import { button, el, selectField, textField, withBusy } from '../../ui/dom';
import { errorState, loadingState } from '../../ui/states';
import { errorMessage } from '../../ui/toast';

export async function renderCollectPage(ctx: PageContext): Promise<Teardown> {
  const token = ctx.params.token!;
  ctx.host.appendChild(loadingState('Opening the form'));

  let info: CollectInfo;
  try {
    info = await api.collectInfo(token);
  } catch (err) {
    ctx.host.replaceChildren(badLink(err));
    return;
  }

  const col = el('div', { class: 'sheet__col' });
  ctx.host.replaceChildren(el('div', { class: 'sheet' }, col));

  const year = new Date().getFullYear();
  const seal = new RingSeal({ size: 128, year, mark: null });

  const name = textField({
    label: 'Your name',
    name: 'title',
    placeholder: 'Anna Lindqvist',
    maxlength: '120',
    autocomplete: 'name',
    required: true,
  });
  const month = selectField({
    label: 'Month',
    name: 'month',
    options: MONTH_NAMES.map((m, i) => ({ value: String(i + 1), label: m })),
    value: '1',
  });
  const day = selectField({ label: 'Day', name: 'day', options: [] });
  const birthYear = textField({
    label: 'Year of birth',
    name: 'year',
    inputmode: 'numeric',
    maxlength: '4',
    placeholder: 'optional',
    hint: 'Only so the ring can show the age you are turning. Leave it blank and it shows your name.',
  });

  const photo = new PhotoPicker({
    label: 'Photo',
    upload: (file) => api.collectImage(token, file),
  });

  const summary = el('div', { class: 'field__error', role: 'alert' });
  summary.hidden = true;

  const submit = el('button', { class: 'btn btn--primary', type: 'submit', text: 'Add me' });

  function fillDays(): void {
    const m = Number(month.input.value) || 1;
    const count = daysInMonth(2024, m);
    const previous = Number(day.input.value) || 1;
    day.input.replaceChildren(
      ...Array.from({ length: count }, (_, i) => el('option', { value: String(i + 1), text: String(i + 1) })),
    );
    day.input.value = String(Math.min(previous, count));
    updateSeal();
  }

  function updateSeal(): void {
    seal.setMark({ month: Number(month.input.value), day: Number(day.input.value) });
  }

  month.input.addEventListener('change', fillDays);
  day.input.addEventListener('change', updateSeal);
  fillDays();

  const form = el(
    'form',
    { class: 'collect-form', novalidate: true },
    name.root,
    el('div', { class: 'field-row field-row--date' }, month.root, day.root, birthYear.root),
    el('div', { class: 'collect__seal' }, seal.root),
    photo.root,
    summary,
    el('div', { class: 'form-actions' }, submit, el('span', { class: 'grow' })),
  );

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void send();
  });

  async function send(): Promise<void> {
    summary.hidden = true;
    const rawYear = birthYear.input.value.trim();
    const body = {
      title: name.input.value.trim(),
      month: Number(month.input.value),
      day: Number(day.input.value),
      ...(rawYear ? { year: Number(rawYear) } : {}),
      ...(photo.value ? { imageKey: photo.value } : {}),
    };
    const result = validate(collectSubmitSchema, body);
    name.setError(result.ok ? null : (result.errors.title ?? null));
    birthYear.setError(result.ok ? null : (result.errors.year ?? null));
    if (!result.ok) {
      summary.textContent = result.message;
      summary.hidden = false;
      return;
    }

    await withBusy(submit, async () => {
      try {
        await api.collectSubmit(token, result.data);
        photo.destroy();
        showThanks();
      } catch (err) {
        summary.textContent = sendError(err);
        summary.hidden = false;
      }
    });
  }

  function showThanks(): void {
    col.replaceChildren(
      el(
        'div',
        { class: 'thanks' },
        seal.root,
        el('h1', { text: 'You are on the list' }),
        el('p', {
          class: 'lede',
          text: `${info.calendarName} will see your birthday next time they look at the ring. Nothing shows up until they say yes.`,
        }),
        el('a', { class: 'btn', href: '/', text: 'Make a ring of your own' }),
      ),
    );
  }

  col.append(
    el(
      'div',
      { class: 'stack', style: 'gap:10px' },
      el('h1', { text: 'Add your birthday' }),
      el('p', {
        class: 'lede',
        text: `${info.calendarName} keeps a ring of the year. Put yourself on it — “${info.sourceName}”.`,
      }),
    ),
    form,
    el(
      'div',
      { class: 'page-foot' },
      el('span', { text: 'Your name and date go to the calendar owner. Nothing is public.' }),
    ),
  );
  name.input.focus();

  return () => photo.destroy();
}

function sendError(err: unknown): string {
  if (err instanceof ApiError && err.status === 429) {
    const wait = err.retryAfter ? ` Try again in ${Math.ceil(err.retryAfter / 60)} min.` : ' Try again a bit later.';
    return `That is a lot of submissions from here.${wait}`;
  }
  return errorMessage(err, 'That did not go through. Check your connection and try again.');
}

function badLink(err: unknown): HTMLElement {
  const home = el('a', { class: 'btn', href: '/', text: 'What is Circles?' });
  if (err instanceof ApiError && err.status === 404) {
    return errorState({
      title: 'This link has expired',
      description: 'The person who sent it has replaced or removed it. Ask them for the current one.',
      actions: [home],
    });
  }
  return errorState({
    title: 'The form did not load',
    description: errorMessage(err),
    actions: [home],
  });
}
